import { useReducer, type ChangeEvent } from 'react';
import { z } from 'zod';

import { passwordRegex } from '../../activateForm/activationForm';
import { passwordRequireMessage } from '../../hsbc/steps/defaultRegistration/step_3/passwordSetupForm';

const passwordSchema = z.string().check(ctx => {
	if (ctx.value.length < 10) {
		ctx.issues.push({
			code: 'custom',
			message: passwordRequireMessage,
			input: ctx.value,
		});
	} else if (!passwordRegex.test(ctx.value)) {
		ctx.issues.push({
			code: 'custom',
			message: passwordRequireMessage,
			input: ctx.value,
		});
	}
});

type State = {
	passwordValue: string;
	isPasswordError: boolean;
	confirmPasswordValue: string;
	isConfirmPasswordError: boolean;
	isPasswordBlurred: boolean;
	isConfirmPasswordBlurred: boolean;
};

type Action =
	| { type: 'SET_PASSWORD'; payload: string }
	| { type: 'SET_PASSWORD_ERROR'; payload: boolean }
	| { type: 'SET_CONFIRM_PASSWORD'; payload: string }
	| { type: 'SET_CONFIRM_PASSWORD_ERROR'; payload: boolean }
	| { type: 'SET_PASSWORD_BLURRED'; payload: boolean }
	| { type: 'SET_CONFIRM_PASSWORD_BLURRED'; payload: boolean }
	| { type: 'SET_PASSWORD_CLEAR' }
	| { type: 'RESET' };

type PasswordInputProps = {
	state: State;
	handlePasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handlePasswordValidation: () => void;
	handleConfirmPasswordValidation: () => void;
	resetPasswordFormState: () => void;
	clearPasswordState: () => void;
};

const initialState: State = {
	passwordValue: '',
	isPasswordError: false,
	confirmPasswordValue: '',
	isConfirmPasswordError: false,
	isPasswordBlurred: false,
	isConfirmPasswordBlurred: false,
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'SET_PASSWORD': {
			return { ...state, passwordValue: action.payload };
		}
		case 'SET_PASSWORD_ERROR': {
			return { ...state, isPasswordError: action.payload };
		}
		case 'SET_CONFIRM_PASSWORD': {
			return { ...state, confirmPasswordValue: action.payload };
		}
		case 'SET_CONFIRM_PASSWORD_ERROR': {
			return { ...state, isConfirmPasswordError: action.payload };
		}
		case 'SET_PASSWORD_BLURRED': {
			return { ...state, isPasswordBlurred: action.payload };
		}
		case 'SET_PASSWORD_CLEAR': {
			return { ...state, passwordValue: '', isPasswordError: false };
		}
		case 'SET_CONFIRM_PASSWORD_BLURRED': {
			return { ...state, isConfirmPasswordBlurred: action.payload };
		}
		case 'RESET': {
			return initialState;
		}
		default: {
			return state;
		}
	}
};

export const usePasswordInput = (): PasswordInputProps => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const handlePasswordValidation = () => {
		dispatch({ type: 'SET_PASSWORD_BLURRED', payload: true });
		if (state.passwordValue.length > 0) {
			const isPasswordError = !passwordSchema.safeParse(state.passwordValue).success;
			dispatch({ type: 'SET_PASSWORD_ERROR', payload: isPasswordError });
		} else {
			dispatch({ type: 'SET_PASSWORD_ERROR', payload: false });
		}
	};

	const handleConfirmPasswordValidation = () => {
		dispatch({ type: 'SET_CONFIRM_PASSWORD_BLURRED', payload: true });
		if (state.confirmPasswordValue.length > 0) {
			const isConfirmPasswordError =
				!passwordSchema.safeParse(state.confirmPasswordValue).success ||
				state.confirmPasswordValue !== state.passwordValue;
			dispatch({ type: 'SET_CONFIRM_PASSWORD_ERROR', payload: isConfirmPasswordError });
		} else {
			dispatch({ type: 'SET_CONFIRM_PASSWORD_ERROR', payload: false });
		}
	};

	const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		if (name === 'password') {
			dispatch({ type: 'SET_PASSWORD', payload: value });
			dispatch({ type: 'SET_PASSWORD_BLURRED', payload: false });
		} else {
			dispatch({ type: 'SET_CONFIRM_PASSWORD', payload: value });
			dispatch({ type: 'SET_CONFIRM_PASSWORD_BLURRED', payload: false });

			const isConfirmPasswordError = !passwordSchema.safeParse(value).success || value !== state.passwordValue;
			dispatch({ type: 'SET_CONFIRM_PASSWORD_ERROR', payload: isConfirmPasswordError });
		}
	};

	const resetPasswordFormState = () => {
		dispatch({ type: 'RESET' });
	};

	const clearPasswordState = () => {
		dispatch({ type: 'SET_PASSWORD_CLEAR' });
	};

	return {
		state,
		handlePasswordChange,
		handlePasswordValidation,
		handleConfirmPasswordValidation,
		resetPasswordFormState,
		clearPasswordState,
	};
};
